import { notifications } from '@mantine/notifications';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormAttachmentViewer } from 'src/Components/Forms/FormAttachmentViewer';
import { UploadFileButton } from 'src/Components/Inputs/UploadFileButton';
import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';
import { FileInfoDto } from 'src/Services/API/generated';

import styles from './PhotoAttachmentsField.module.scss';
import { Props } from './PhotoAttachmentsField.types';

const toAttachment = (file: FileInfoDto, video = file.video): FormAttachment => ({
  video,
  fileId: file.id,
  type: file.type,
  name: file.name,
  previewUrl: file.urls?.previewThumbnailUrl,
});

/**
 * Photos of a form, added like the main photo of an instruction: the upload button of the app
 * (take a photo, import, or link a document of Documents) and the thumbnails of
 * FormAttachmentViewer. Every file lives in Documents.
 */
const PhotoAttachmentsField = ({
  value,
  onChange,
  multiple = true,
  label,
  modalTitle,
  fileUploadLabel,
  existingFileLabel,
  uploadFolder,
  imagesOnly = true,
  max,
  firstBadge,
  sortable,
  loading,
  onUploadingChange,
  error,
}: Props) => {
  const { t } = useTranslation();
  // Several uploads end in the same tick: each one adds to the latest list, not to a stale one.
  const valueRef = useRef(value);
  valueRef.current = value;

  const add = (attachments: FormAttachment[]) => {
    if (!multiple) {
      const [attachment] = attachments;
      if (!attachment) return;
      valueRef.current = [attachment];
      onChange(valueRef.current);
      return;
    }
    const current = valueRef.current;
    const added = attachments.filter(
      (attachment, index) =>
        !current.some(existing => existing.fileId === attachment.fileId) &&
        attachments.findIndex(other => other.fileId === attachment.fileId) === index
    );
    const next = [...current, ...added].slice(0, max);
    valueRef.current = next;
    onChange(next);
  };

  const notifyNotAPhoto = () =>
    notifications.show({
      color: 'red',
      message: t('photoAttachmentsField.notAPhoto', 'Choisissez une photo.'),
    });

  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();
  const { uploads, startUpload, removeUpload, isLoading } = useFileUploadManager({
    onUploadReady: readyFile => {
      if (!readyFile.fileData) {
        console.error('readyFile.fileData should not be undefined.');
        return;
      }
      add([toAttachment(readyFile.fileData, readyFile.videoData)]);
      removeUpload(readyFile.localId);
    },
  });

  useEffect(() => {
    onUploadingChange?.(isLoading);
  }, [isLoading, onUploadingChange]);

  const upload = (files: File[]) => {
    const accepted = imagesOnly ? files.filter(file => file.type.startsWith('image/')) : files;
    if (accepted.length < files.length) notifyNotAPhoto();
    const room = multiple && max !== undefined ? max - valueRef.current.length : accepted.length;
    if (accepted.length > 0 && room > 0) startUpload(accepted.slice(0, room), uploadFolder);
  };

  const linkExisting = async (fileIds: string[]) => {
    try {
      const files = await Promise.all(fileIds.map(fileId => fetchFileInfo(fileId)));
      // Only images get a preview when they are filed in Documents.
      const accepted = imagesOnly
        ? files.filter(file => !!file.urls?.previewThumbnailUrl && !file.video)
        : files;
      if (accepted.length < files.length) notifyNotAPhoto();
      add(accepted.map(file => toAttachment(file)));
    } catch {
      notifications.show({
        color: 'red',
        message: t(
          'photoAttachmentsField.linkError',
          "La photo n'a pas pu être ajoutée. Réessayez."
        ),
      });
    }
  };

  const viewer = (
    <FormAttachmentViewer
      attachments={value}
      uploadingAttachments={uploads}
      onDeleteAttachment={attachment =>
        onChange(valueRef.current.filter(current => current.fileId !== attachment.fileId))
      }
      onDeleteUploadingAttachment={localId => removeUpload(localId)}
      firstBadge={firstBadge}
      onReorder={sortable ? onChange : undefined}
    />
  );

  const buttonProps = {
    loading: isLoading || !!loading,
    label,
    modalTitle,
    fileUploadLabel,
    existingFileLabel,
  };

  if (!multiple) {
    // The main photo of an instruction: the photo replaces the button.
    return (
      <>
        {value.length > 0 ? (
          viewer
        ) : (
          <UploadFileButton
            {...buttonProps}
            single
            onFileSelected={file => upload([file])}
            onExistingFileSelect={fileId => linkExisting([fileId])}
          />
        )}
        {error && <div className={styles.error}>{error}</div>}
      </>
    );
  }

  return (
    <div className={styles.container}>
      {(max === undefined || value.length < max) && (
        <UploadFileButton
          {...buttonProps}
          onFilesSelected={upload}
          onExistingFilesSelect={linkExisting}
        />
      )}
      {value.length > 0 && viewer}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default PhotoAttachmentsField;
